/**
 * Cart drawer — section-swap sync helpers.
 *
 * The cart drawer routinely replaces #CartDrawer .drawer__inner innerHTML in
 * response to Shopify AJAX section renders (cart add, remove, line change,
 * quantity update). The AJAX render is *not always complete*: overlay-group
 * blocks (upsell, gift selector, progress bar) can be missing when the
 * section is fetched via ?section_id=cart-drawer because the block context
 * isn't available to that endpoint. These helpers:
 *
 *   • Capture live transition-carrying nodes (progress bar, upsell) before
 *     the swap so the same DOM node survives the innerHTML replacement and
 *     CSS transitions carry over seamlessly.
 *   • Coalesce concurrent section fetches into a single in-flight request.
 *   • Animate progress-bar width transitions using the captured pre-swap
 *     visual position as a starting point.
 *
 * All top-level declarations here live in the classic-script "realm scope"
 * shared with cart.js, cart-drawer-gift-safety.js, and cart-drawer-progress-
 * reconcile.js, so any function declared here is callable from any of them
 * at runtime without `window.` prefixing.
 */

/**
 * Progress bar smooth-transition helpers.
 * Captures the *computed* (visual) width before DOM swap so
 * interrupted animations still transition smoothly from the
 * current on-screen position.
 */
let _lastDrawerUpdate = 0;
const DRAWER_UPDATE_GUARD_MS = 320;

/**
 * Monotonically increasing counter bumped every time renderContents runs.
 * onCartUpdate captures the value before its fetch and skips the DOM swap
 * if the counter changed while the request was in-flight — meaning
 * renderContents already applied a fresher update.
 */
let _drawerVersion = 0;

/**
 * Coalesce concurrent `?section_id=cart-drawer` fetches (gift refresh, upsell
 * repair, progress reinjection) into a single in-flight request. Callers
 * needing fresh HTML right after a mutation null `_cartDrawerSectionHtmlPromise`
 * directly (cart-drawer-gift-safety's cart:updated handler does this).
 */
let _cartDrawerSectionHtmlPromise = null;
function fetchCartDrawerSectionHtml() {
  if (typeof routes === 'undefined' || !routes.cart_url) {
    return Promise.reject(new Error('cart_url unavailable'));
  }
  if (!_cartDrawerSectionHtmlPromise) {
    _cartDrawerSectionHtmlPromise = fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText || 'cart-drawer section fetch failed');
        return response.text();
      })
      .finally(() => { _cartDrawerSectionHtmlPromise = null; });
  }
  return _cartDrawerSectionHtmlPromise;
}

/**
 * Captures the live progress bar element so we can re-insert it *into* the
 * new DOM after a section swap. Because the same DOM node stays in the
 * document, CSS transitions (fill width, shadow, glow) carry over seamlessly
 * — no flicker, no reset, no "break" when a line item is removed.
 */
function captureLiveProgressNode() {
  return document.getElementById('CartDrawer-Progress');
}

/**
 * Ensures the header wrapper for the tiered progress lane exists (some AJAX
 * cart-drawer renders omit overlay-group blocks entirely).
 */
function ensureCartDrawerProgressBleed() {
  let bleed = document.querySelector('#CartDrawer .cart-drawer__progress-bleed');
  if (bleed) return bleed;
  const header = document.querySelector('#CartDrawer .drawer__header');
  const headerRow = header && header.querySelector('.cart-drawer__header-row');
  if (!header || !headerRow) return null;
  bleed = document.createElement('div');
  bleed.className = 'cart-drawer__progress-bleed';
  headerRow.insertAdjacentElement('afterend', bleed);
  return bleed;
}

/**
 * After a section HTML swap, find the freshly-rendered progress bar and
 * replace it with the original live node. **The live node's structure,
 * labels, icons, and merchant-configured copy are treated as authoritative
 * and permanent** — the ONLY thing allowed to change across a cart update
 * is the dynamic state: how full the bar is, which tiers are marked
 * complete, and the headline text ("Spend $X more...").
 *
 * Why this matters: the AJAX `?section_id=cart-drawer` render occasionally
 * produces a stripped-down / default progress bar when overlay-group block
 * settings aren't fully available in the section render context. The old
 * implementation copied `.cart-drawer__progress-labels` innerHTML from that
 * stripped render straight into the live node, which wiped the merchant's
 * milestone labels (and any custom styling) the moment a product was
 * removed. That's the bug. We now derive only the dynamic facts from the
 * fresh render and leave every label verbatim.
 */
function mergeLiveProgressNode(liveNode) {
  if (!liveNode) return;
  let freshNode = document.getElementById('CartDrawer-Progress');

  // AJAX cart-drawer HTML sometimes omits #CartDrawer-Progress entirely (no
  // overlay block context). The captured node is then orphaned after innerHTML
  // / replaceWith — getElementById returns null. Re-mount the live bar so the
  // merchant-enabled progress UI stays permanent whenever data-cfg-progress.
  if (!freshNode) {
    const drawer = document.getElementById('CartDrawer');
    if (
      drawer &&
      drawer.getAttribute('data-cfg-progress') === 'true' &&
      liveNode.id === 'CartDrawer-Progress'
    ) {
      const bleed = ensureCartDrawerProgressBleed();
      if (bleed && !bleed.contains(liveNode)) {
        bleed.appendChild(liveNode);
      }
    }
    return;
  }

  if (freshNode === liveNode) return;

  // Headline text — this IS dynamic ("Spend $X more...", "All unlocked!").
  const liveHead = liveNode.querySelector('.cart-drawer__progress-headline');
  const freshHead = freshNode.querySelector('.cart-drawer__progress-headline');
  if (liveHead && freshHead) {
    const freshText = (freshHead.textContent || '').trim();
    // Only adopt the fresh headline if it actually has content. A blank/empty
    // fresh headline means the AJAX section render lost overlay-group block
    // context — keep whatever merchant-configured copy is already live.
    // The client-side reconcile listener (reconcileCartDrawerProgressHeadline)
    // is the authoritative updater; this merge is now only a best-effort
    // passthrough when the server render is healthy.
    if (freshText.length > 0) {
      liveHead.textContent = freshText;
    }
  }

  // Fill width — animates via CSS transition since the element is the same.
  const liveFill = liveNode.querySelector('.cart-drawer__progress-fill');
  const freshFill = freshNode.querySelector('.cart-drawer__progress-fill');
  if (liveFill && freshFill && freshFill.style.width) {
    liveFill.style.width = freshFill.style.width;
  }

  // aria-valuenow on the track for assistive tech.
  const liveTrack = liveNode.querySelector('.cart-drawer__progress-track');
  const freshTrack = freshNode.querySelector('.cart-drawer__progress-track');
  if (liveTrack && freshTrack) {
    const v = freshTrack.getAttribute('aria-valuenow');
    if (v != null) liveTrack.setAttribute('aria-valuenow', v);
  }

  // Pin completion state — toggle `--complete` on each pin so the
  // disc fills/unfills as thresholds are crossed, without touching
  // the pin's inline style (left %), icon contents, or any other
  // merchant-authored classes.
  const livePins = liveNode.querySelectorAll('.cart-drawer__pin');
  const freshPins = freshNode.querySelectorAll('.cart-drawer__pin');
  livePins.forEach((pin, i) => {
    const np = freshPins[i];
    if (!np) return;
    pin.classList.toggle(
      'cart-drawer__pin--complete',
      np.classList.contains('cart-drawer__pin--complete')
    );
  });

  // Label-slot completion state — same idea as pins. Toggle ONLY the
  // `--complete` class; leave `--start`, `--end`, layout left %, and
  // the entire `.cart-drawer__label-text` (the merchant's configured
  // label copy) untouched. This is the fix: previously we overwrote the
  // whole labels subtree from the fresh server render and that's what
  // wiped the custom labels/styling whenever an item was removed.
  const liveSlots = liveNode.querySelectorAll('.cart-drawer__label-slot');
  const freshSlots = freshNode.querySelectorAll('.cart-drawer__label-slot');
  liveSlots.forEach((slot, i) => {
    const ns = freshSlots[i];
    if (!ns) return;
    slot.classList.toggle(
      'cart-drawer__label-slot--complete',
      ns.classList.contains('cart-drawer__label-slot--complete')
    );
  });

  // Top-level `--all-complete` modifier governs the "all unlocked" glow +
  // headline styling. Toggle just that flag — do NOT do a wholesale
  // className swap (that would nuke any merchant-added classes on the
  // root, plus the tier modifier `--m2`/`--m3` which never changes for
  // a given store config anyway).
  liveNode.classList.toggle(
    'cart-drawer__progress--all-complete',
    freshNode.classList.contains('cart-drawer__progress--all-complete')
  );

  // Swap the fresh (detached) node out for our live (transition-carrying,
  // label-preserving) one.
  if (freshNode.parentNode) freshNode.parentNode.replaceChild(liveNode, freshNode);
}

/**
 * Gift chooser "takeover" adds .gift-chooser-active on #CartDrawer .drawer__inner.
 * Cart line updates replace .drawer__inner children only — the class survives and CSS
 * hides the FREE GIFTS bar, header, and line items. cart-drawer renderContents() clears
 * this; mirror whenever the drawer body is refreshed without replacing .drawer__inner.
 */
function clearCartDrawerGiftChooserTakeover() {
  const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
  if (drawerInner) drawerInner.classList.remove('gift-chooser-active');
}

/**
 * Cart Ajax section HTML often omits overlay-group blocks (upsell). Detach before
 * .drawer__inner innerHTML is replaced; re-insert before the footer if missing after.
 */
function captureLiveUpsellNode() {
  return document.querySelector('#CartDrawer cart-drawer-upsell');
}

/*
 * Empty-cart hiding for the upsell is now owned entirely by CSS
 * (component-cart-drawer.css cart-drawer-items.is-empty / cart-drawer.is-empty
 * hide everything in .cart-drawer__footer except the gift selector). The
 * earlier removeCartDrawerUpsell()/cartDrawerShouldHideUpsell() helpers were
 * called on empty-cart transitions and tore the upsell out of the DOM —
 * which was the root cause of "upsell disappears after emptying the cart
 * and adding a new product". Once removed, the only path back was an AJAX
 * section refetch, and that fetch occasionally returned HTML without the
 * overlay-group upsell block. The node is now kept mounted across all
 * cart states; CSS handles visibility.
 */

/**
 * When Ajax cart HTML swaps omit `cart-drawer-upsell`, we preserve the live custom
 * element so carousel state survives — then re-fetch the full section markup so
 * "in cart" badges stay aligned with `cart.items`.
 */
function refreshCartDrawerUpsellFromSection() {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer || typeof routes === 'undefined' || !routes.cart_url) return;

  fetchCartDrawerSectionHtml()
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceUpsell = html.querySelector('cart-drawer-upsell');
      if (!sourceUpsell) return;
      const targetUpsell = drawer.querySelector('cart-drawer-upsell');
      if (targetUpsell) {
        targetUpsell.replaceWith(sourceUpsell);
        return;
      }
      insertUpsellAboveOrderSummary(drawer, sourceUpsell);
    })
    .catch((err) => { if (window.DEBUG_CART) console.warn('[cart] upsell refresh: section fetch failed', err); });
}

function insertUpsellAboveOrderSummary(drawer, upsellNode) {
  const innerFooter = drawer.querySelector('.cart-drawer__footer');
  const orderSummary = drawer.querySelector('.cart-drawer__order-summary');
  if (innerFooter && orderSummary && orderSummary.parentNode === innerFooter) {
    innerFooter.insertBefore(upsellNode, orderSummary);
  } else if (innerFooter) {
    innerFooter.appendChild(upsellNode);
  } else {
    const footer = drawer.querySelector('.drawer__footer');
    if (footer) footer.prepend(upsellNode);
  }
}

function mergeLiveUpsellNode(liveNode) {
  if (!liveNode) return false;
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return false;

  /* Empty-cart state is handled by CSS (cart-drawer-items.is-empty /
     cart-drawer.is-empty selectors in component-cart-drawer.css hide every
     drawer-footer child except the gift selector). Keep the upsell node in
     the DOM regardless of cart state so transitioning from empty → has-items
     is a pure CSS class flip — no fragile re-fetch race. */
  const fresh = drawer.querySelector('cart-drawer-upsell');
  if (fresh) {
    liveNode.remove();
    return false;
  }
  liveNode.setAttribute('data-needs-refresh', 'true');
  insertUpsellAboveOrderSummary(drawer, liveNode);
  return !!liveNode.isConnected;
}

/**
 * Cart/add.js section fragments sometimes omit `cart-drawer-upsell`. Fetch the full
 * cart-drawer section and insert the upsell node before the footer when it is missing
 * and the cart is not empty.
 */
function ensureCartDrawerUpsellFromSection(parsedState) {
  const drawer = document.getElementById('CartDrawer');
  const cartDrawer = document.querySelector('cart-drawer');
  if (!drawer || !cartDrawer) return;
  /* Don't gate on cart-empty: keep the upsell node mounted whenever the
     merchant has it configured. CSS handles hiding the strip on empty cart,
     so we never tear it down and never have to re-acquire it on re-add. */
  if (drawer.querySelector('cart-drawer-upsell')) return;
  if (typeof routes === 'undefined' || !routes.cart_url) return;

  fetchCartDrawerSectionHtml()
    .then((responseText) => {
      if (document.querySelector('#CartDrawer cart-drawer-upsell')) return;

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceUpsell = html.querySelector('cart-drawer-upsell');
      if (!sourceUpsell) return;
      insertUpsellAboveOrderSummary(drawer, sourceUpsell);
    })
    .catch((err) => { if (window.DEBUG_CART) console.warn('[cart] upsell ensure: section fetch failed', err); });
}

function captureProgressState() {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return null;
  const fill = drawer.querySelector('.cart-drawer__progress-fill');
  const track = drawer.querySelector('.cart-drawer__progress-track');
  if (!fill || !track) return null;

  // Use computed width (mid-animation aware) converted to percentage
  const trackW = track.getBoundingClientRect().width;
  const fillW = fill.getBoundingClientRect().width;
  const pct = trackW > 0 ? Math.round((fillW / trackW) * 10000) / 100 : 0;

  return {
    fillPct: pct + '%',
    allComplete: drawer.querySelector('.cart-drawer__progress--all-complete') !== null,
  };
}

function animateProgressTransition(prev) {
  if (!prev) return;
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;
  const fill = drawer.querySelector('.cart-drawer__progress-fill');
  const track = drawer.querySelector('.cart-drawer__progress-track');
  if (!fill) return;

  // Prefer inline width (set by merge + reconcile). When reconcile skipped a
  // bad payload, the bar may only have layout width — mirror captureProgressState.
  let newWidth = (fill.style && fill.style.width) ? String(fill.style.width).trim() : '';
  if (!newWidth && track) {
    const trackW = track.getBoundingClientRect().width;
    const fillW = fill.getBoundingClientRect().width;
    if (trackW > 0) newWidth = (Math.round((fillW / trackW) * 10000) / 100) + '%';
  }
  if (!newWidth) return;

  // Skip if widths are effectively the same (avoids no-op flashes)
  if (newWidth === prev.fillPct) return;

  // Set to captured visual position (no transition)
  fill.style.transition = 'none';
  fill.style.width = prev.fillPct;
  fill.offsetWidth; // force reflow

  // Restore transition and set new width — CSS transition animates
  fill.style.transition = '';
  fill.style.width = newWidth;

  // Suppress glow re-play if bar was already complete
  if (prev.allComplete && drawer.querySelector('.cart-drawer__progress--all-complete')) {
    fill.style.animation = 'none';
  }
  _lastDrawerUpdate = Date.now();
}

/**
 * Merchandise spend used for gift thresholds + progress bar.
 *
 * Sums `original_line_price` for every line whose `_drawer_gift_selector`
 * property is unset — i.e. non-gift items at their pre-discount line price.
 *
 * Why not `items_subtotal_price - gift_line`: Shopify's modern free-gift
 * Function applies as a LINE-ITEM discount, which means
 * `items_subtotal_price` already excludes the gift's price. Subtracting
 * the gift line a second time pulled qualifying spend below threshold the
 * instant the gift landed, removed the gift, the bar climbed back over
 * threshold, the controller re-added — endless add/remove loop.
 *
 * `original_line_price` is the pre-all-discounts price, so the answer is
 * stable regardless of how (or whether) Shopify chose to discount the
 * gift line.
 */
function qualifyingSpendCentsFromCart(cart) {
  if (!cart || !Array.isArray(cart.items)) return 0;
  let q = 0;
  for (let i = 0; i < cart.items.length; i++) {
    const it = cart.items[i];
    const props = it && it.properties;
    if (props && props._drawer_gift_selector) continue;
    const px =
      typeof it.original_line_price === 'number' ? it.original_line_price :
      typeof it.line_price === 'number' ? it.line_price :
      typeof it.final_line_price === 'number' ? it.final_line_price : 0;
    q += px;
  }
  return q;
}

if (typeof window !== 'undefined') {
  // Exposed so outside callers (cart-drawer.js, cart-drawer-progress.liquid
  // inline scripts) can reach it without depending on classic-script realm
  // scoping. Internal callers still use the bare name.
  window.qualifyingSpendCentsFromCart = qualifyingSpendCentsFromCart;
}
